import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  Loader2,
  MessageSquare,
  ArrowUpRight,
  Megaphone,
  Send,
  Edit3,
  Trash2,
  X,
  Check,
  CornerDownRight,
  Reply,
} from "lucide-react";
import { taskCommentsApi, projectPostsApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import MentionsTextarea, { renderMentions } from "@/components/MentionsTextarea";

// ProjectDiscussionPage — Feb 2026.
//
// Merged chronological feed of:
//   - Task comments (deep-link → opens task dialog)
//   - General project posts (no task anchor — pure announcements)
// Composer at top lets you publish a project-wide post.

const PHASE_PILL = {
  pre: { label: "PRE", bg: "bg-teal-500/15 text-teal-300 border-teal-500/40" },
  prod: { label: "PROD", bg: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  post: { label: "POST", bg: "bg-violet-500/15 text-violet-300 border-violet-500/40" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.floor((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function groupByDay(items) {
  const out = [];
  let lastKey = null;
  for (const item of items) {
    const day = (item.created_at || "").slice(0, 10);
    if (day !== lastKey) {
      out.push({ type: "header", date: day });
      lastKey = day;
    }
    out.push({ type: "item", item });
  }
  return out;
}

// Split the flat feed into top-level items + a parent_id → [replies] map.
// Top-level items keep the server's newest-first order. Replies under each
// parent are sorted OLDEST first so a thread reads naturally top-to-bottom.
function splitThreads(feed) {
  const topLevel = [];
  const repliesByParent = {};
  for (const item of feed || []) {
    if (item.parent_id) {
      (repliesByParent[item.parent_id] ||= []).push(item);
    } else {
      topLevel.push(item);
    }
  }
  for (const pid in repliesByParent) {
    repliesByParent[pid].sort((a, b) =>
      (a.created_at || "").localeCompare(b.created_at || ""),
    );
  }
  return { topLevel, repliesByParent };
}

export default function ProjectDiscussionPage() {
  const { id: projectId } = useParams();
  const { project } = useOutletContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canPost = !!project?.my_role; // viewers, editors, and admins all post
  const isAdmin = user?.role === "admin";

  const [data, setData] = useState({
    feed: [],
    task_index: {},
    members: [],
  });
  const [loading, setLoading] = useState(true);

  // Composer state
  const [postText, setPostText] = useState("");
  const [postMentions, setPostMentions] = useState([]);
  const [posting, setPosting] = useState(false);
  const [composerExpanded, setComposerExpanded] = useState(false);

  // Reply state — single open composer at a time keyed by parent id
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyMentions, setReplyMentions] = useState([]);
  const [replySending, setReplySending] = useState(false);

  // Edit state for any post the current user owns
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editMentions, setEditMentions] = useState([]);

  const reload = async () => {
    try {
      const d = await taskCommentsApi.discussion(projectId);
      setData(d || { feed: [], task_index: {}, members: [] });
    } catch {
      toast.error("Failed to load discussion");
    }
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    taskCommentsApi
      .discussion(projectId)
      .then((d) => mounted && setData(d || { feed: [], task_index: {}, members: [] }))
      .catch(() => toast.error("Failed to load discussion"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [projectId]);

  const handlePost = async () => {
    const trimmed = postText.trim();
    if (!trimmed) return;
    setPosting(true);
    try {
      await projectPostsApi.create(projectId, trimmed, postMentions);
      setPostText("");
      setPostMentions([]);
      setComposerExpanded(false);
      await reload();
      toast.success(
        postMentions.length
          ? `Posted — ${postMentions.length} ${postMentions.length === 1 ? "person" : "people"} notified`
          : "Posted to the project",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post");
    } finally {
      setPosting(false);
    }
  };

  const openTask = (taskId) => {
    navigate(`/projects/${projectId}/plan?task=${taskId}`);
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditText(item.text);
    setEditMentions(item.mentions || []);
  };

  const handleSaveEdit = async (item) => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    try {
      if (item.kind === "post") {
        await projectPostsApi.edit(item.id, trimmed, editMentions);
      } else {
        await taskCommentsApi.edit(item.id, trimmed, editMentions);
      }
      setEditingId(null);
      setEditText("");
      setEditMentions([]);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to edit");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm("Delete this?")) return;
    try {
      if (item.kind === "post") {
        await projectPostsApi.remove(item.id);
      } else {
        await taskCommentsApi.remove(item.id);
      }
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const startReply = (parent) => {
    setReplyingTo(parent);
    setReplyText("");
    setReplyMentions([]);
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setReplyText("");
    setReplyMentions([]);
  };

  const sendReply = async () => {
    if (!replyingTo) return;
    const trimmed = replyText.trim();
    if (!trimmed) return;
    setReplySending(true);
    try {
      if (replyingTo.kind === "post") {
        await projectPostsApi.create(
          projectId,
          trimmed,
          replyMentions,
          replyingTo.id,
        );
      } else {
        await taskCommentsApi.create(
          replyingTo.task_id,
          trimmed,
          replyMentions,
          replyingTo.id,
        );
      }
      cancelReply();
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reply");
    } finally {
      setReplySending(false);
    }
  };

  const { topLevel, repliesByParent } = splitThreads(data.feed || []);
  const grouped = groupByDay(topLevel);

  return (
    <div className="max-w-screen-md mx-auto px-6 lg:px-10 py-8">
      <header className="mb-6">
        <div className="text-overline text-amber-500/80 mb-1 flex items-center gap-1.5">
          <MessageSquare className="w-3 h-3" /> Discussion
        </div>
        <h1 className="font-heading text-3xl font-light text-zinc-100 leading-tight">
          What&apos;s been said lately
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Every comment + post across the project. Use <span className="font-mono text-amber-400">@</span> to ping someone directly.
        </p>
      </header>

      {/* Composer */}
      {canPost && (
        <div
          data-testid="post-composer"
          className="border border-zinc-800 bg-zinc-950/60 rounded-md p-4 mb-6"
          style={{ borderLeftColor: "#f59e0b", borderLeftWidth: 3 }}
        >
          <div className="flex items-start gap-3">
            <Megaphone className="w-4 h-4 text-amber-500 mt-2 shrink-0" />
            <div className="flex-1 min-w-0">
              <MentionsTextarea
                data-testid="post-input"
                value={postText}
                onChange={(v) => setPostText(v.slice(0, 8000))}
                onMentionsChange={setPostMentions}
                members={data.members || []}
                placeholder="Share a project-wide update… (@ to mention specific teammates)"
                rows={composerExpanded || postText ? 3 : 1}
                onFocus={() => setComposerExpanded(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handlePost();
                  }
                }}
                wrapperClassName="w-full"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/60 resize-none"
                disabled={posting}
              />
              {(composerExpanded || postText) && (
                <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                  <p className="text-[10px] text-zinc-500">
                    {postMentions.length === 0
                      ? "No mentions — entire project team will be notified."
                      : `${postMentions.length} ${postMentions.length === 1 ? "person" : "people"} will be notified.`}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setPostText("");
                        setPostMentions([]);
                        setComposerExpanded(false);
                      }}
                      className="text-xs text-zinc-400 hover:text-zinc-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-testid="post-send-btn"
                      onClick={handlePost}
                      disabled={!postText.trim() || posting}
                      className="bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 rounded-sm px-3 py-1.5 text-xs font-medium flex items-center gap-1.5"
                    >
                      {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      Post
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Feed */}
      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 text-sm py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (data.feed || []).length === 0 ? (
        <div
          data-testid="discussion-empty"
          className="border border-zinc-800 rounded-md bg-zinc-950/60 p-10 text-center"
        >
          <MessageSquare className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <h2 className="font-heading text-lg font-light text-zinc-300 mb-1">
            Nothing here yet
          </h2>
          <p className="text-xs text-zinc-500">
            Post an update above, or comment on a plan task to start the thread.
          </p>
        </div>
      ) : (
        <div className="space-y-1" data-testid="discussion-feed">
          {grouped.map((row, idx) => {
            if (row.type === "header") {
              return (
                <div
                  key={`h-${row.date}-${idx}`}
                  className="text-[10px] font-mono text-zinc-600 uppercase tracking-wide pt-4 pb-1 border-b border-zinc-900 mb-1"
                >
                  {new Date(row.date + "T12:00:00Z").toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "short",
                    day: "numeric",
                  })}
                </div>
              );
            }
            const it = row.item;
            const isPost = it.kind === "post";
            const task = !isPost ? data.task_index[it.task_id] : null;
            const pill = task ? PHASE_PILL[task.phase] || PHASE_PILL.pre : null;
            const isMine = it.author_user_id === user?.id;
            const isEditing = editingId === it.id;
            const canDelete = isMine || isAdmin;
            const replies = repliesByParent[it.id] || [];
            const isReplyOpen = replyingTo?.id === it.id;
            return (
              <div key={it.id} className="space-y-1.5">
              <article
                data-testid={`discussion-row-${it.id}`}
                className="group bg-zinc-950/40 hover:bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-4 transition-colors"
                style={isPost ? { borderLeftColor: "#f59e0b", borderLeftWidth: 3 } : undefined}
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    {isPost ? (
                      <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase border bg-amber-500/15 text-amber-300 border-amber-500/40 flex items-center gap-1">
                        <Megaphone className="w-2.5 h-2.5" /> Post
                      </span>
                    ) : pill ? (
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase border ${pill.bg}`}>
                        {pill.label}
                      </span>
                    ) : null}
                    {!isPost && (
                      <button
                        type="button"
                        onClick={() => openTask(it.task_id)}
                        className="text-sm font-medium text-zinc-100 hover:text-amber-400 truncate transition-colors flex items-center gap-1"
                      >
                        {task?.name || "Unknown task"}
                        <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    )}
                    {!isPost && task?.kanban_status === "done" && (
                      <span className="text-[10px] font-mono text-emerald-400/70 uppercase">
                        ✓ Done
                      </span>
                    )}
                  </div>
                  {!isEditing && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      {isMine && (
                        <button
                          type="button"
                          data-testid={`row-edit-${it.id}`}
                          onClick={() => handleStartEdit(it)}
                          className="text-zinc-600 hover:text-amber-400 p-1"
                          aria-label="Edit"
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          data-testid={`row-delete-${it.id}`}
                          onClick={() => handleDelete(it)}
                          className="text-zinc-600 hover:text-rose-400 p-1"
                          aria-label="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mb-1.5 flex items-center gap-2">
                  <span className="text-zinc-300">
                    {it.author_name || it.author_email || "Unknown"}
                  </span>
                  <span className="text-zinc-600">·</span>
                  <span className="font-mono text-zinc-600">
                    {timeAgo(it.created_at)}
                    {it.edited && " · edited"}
                  </span>
                </div>
                {isEditing ? (
                  <div className="space-y-2 mt-2">
                    <MentionsTextarea
                      value={editText}
                      onChange={setEditText}
                      onMentionsChange={setEditMentions}
                      members={data.members || []}
                      rows={3}
                      wrapperClassName="w-full"
                      className="w-full bg-zinc-950 border border-zinc-700 rounded-sm px-2 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/60 resize-none"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(it)}
                        className="text-xs bg-amber-500 text-black hover:bg-amber-400 rounded-sm px-2 py-1 flex items-center gap-1 font-medium"
                      >
                        <Check className="w-3 h-3" /> Save
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(null);
                          setEditText("");
                          setEditMentions([]);
                        }}
                        className="text-xs text-zinc-400 hover:text-zinc-200 flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words leading-relaxed">
                    {renderMentions(it.text, data.members || [])}
                  </p>
                )}
                {/* Reply CTA + thread footer */}
                {!isEditing && canPost && (
                  <div className="mt-3 pt-2 border-t border-zinc-900 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      data-testid={`reply-btn-${it.id}`}
                      onClick={() => (isReplyOpen ? cancelReply() : startReply(it))}
                      className="text-[11px] font-mono text-zinc-500 hover:text-amber-400 flex items-center gap-1 transition-colors uppercase tracking-wide"
                    >
                      <Reply className="w-3 h-3" />
                      {isReplyOpen ? "Cancel reply" : "Reply"}
                    </button>
                    {replies.length > 0 && (
                      <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-wide">
                        {replies.length} {replies.length === 1 ? "reply" : "replies"}
                      </span>
                    )}
                  </div>
                )}
              </article>

              {/* Replies — indented, oldest first */}
              {(replies.length > 0 || isReplyOpen) && (
                <div
                  className="ml-6 pl-4 border-l border-zinc-800 space-y-1.5"
                  data-testid={`thread-${it.id}`}
                >
                  {replies.map((reply) => {
                    const replyMine = reply.author_user_id === user?.id;
                    const replyEditing = editingId === reply.id;
                    const replyCanDelete = replyMine || isAdmin;
                    return (
                      <article
                        key={reply.id}
                        data-testid={`reply-row-${reply.id}`}
                        className="group bg-zinc-950/30 hover:bg-zinc-900/30 border border-zinc-900/60 rounded-sm p-3 transition-colors"
                      >
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <div className="flex items-baseline gap-2 min-w-0">
                            <CornerDownRight className="w-3 h-3 text-zinc-600 shrink-0" />
                            <span className="text-xs font-medium text-zinc-200 truncate">
                              {reply.author_name || reply.author_email || "Unknown"}
                            </span>
                            <span className="text-[10px] text-zinc-600 font-mono shrink-0">
                              {timeAgo(reply.created_at)}
                              {reply.edited && " · edited"}
                            </span>
                          </div>
                          {!replyEditing && (
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                              {replyMine && (
                                <button
                                  type="button"
                                  onClick={() => handleStartEdit(reply)}
                                  className="text-zinc-600 hover:text-amber-400 p-1"
                                  aria-label="Edit"
                                >
                                  <Edit3 className="w-3 h-3" />
                                </button>
                              )}
                              {replyCanDelete && (
                                <button
                                  type="button"
                                  onClick={() => handleDelete(reply)}
                                  className="text-zinc-600 hover:text-rose-400 p-1"
                                  aria-label="Delete"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        {replyEditing ? (
                          <div className="space-y-2">
                            <MentionsTextarea
                              value={editText}
                              onChange={setEditText}
                              onMentionsChange={setEditMentions}
                              members={data.members || []}
                              rows={3}
                              wrapperClassName="w-full"
                              className="w-full bg-zinc-950 border border-zinc-700 rounded-sm px-2 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/60 resize-none"
                            />
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleSaveEdit(reply)}
                                className="text-xs bg-amber-500 text-black hover:bg-amber-400 rounded-sm px-2 py-1 flex items-center gap-1 font-medium"
                              >
                                <Check className="w-3 h-3" /> Save
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingId(null);
                                  setEditText("");
                                  setEditMentions([]);
                                }}
                                className="text-xs text-zinc-400 hover:text-zinc-200 flex items-center gap-1"
                              >
                                <X className="w-3 h-3" /> Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words leading-relaxed">
                            {renderMentions(reply.text, data.members || [])}
                          </p>
                        )}
                      </article>
                    );
                  })}

                  {/* Inline reply composer */}
                  {isReplyOpen && (
                    <div
                      data-testid={`reply-composer-${it.id}`}
                      className="bg-zinc-950/40 border border-amber-500/30 rounded-sm p-3"
                    >
                      <MentionsTextarea
                        value={replyText}
                        onChange={setReplyText}
                        onMentionsChange={setReplyMentions}
                        members={data.members || []}
                        placeholder={`Replying to ${it.author_name || "this"}… (@ to mention · ⌘/Ctrl+Enter to send)`}
                        rows={2}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            sendReply();
                          }
                          if (e.key === "Escape") cancelReply();
                        }}
                        wrapperClassName="w-full"
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/60 resize-none"
                        disabled={replySending}
                      />
                      <div className="flex items-center justify-between mt-2 gap-2">
                        <p className="text-[10px] text-zinc-500">
                          {replyMentions.length === 0
                            ? `Only ${it.author_name || "the original author"} will be notified.`
                            : `${replyMentions.length + (it.author_user_id !== user?.id ? 1 : 0)} ${
                                replyMentions.length + (it.author_user_id !== user?.id ? 1 : 0) === 1
                                  ? "person"
                                  : "people"
                              } will be notified.`}
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={cancelReply}
                            className="text-xs text-zinc-400 hover:text-zinc-200"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            data-testid={`reply-send-${it.id}`}
                            onClick={sendReply}
                            disabled={!replyText.trim() || replySending}
                            className="bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 rounded-sm px-3 py-1.5 text-xs font-medium flex items-center gap-1.5"
                          >
                            {replySending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            Reply
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
